import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { StatusBadge, PaymentBadge } from '../components/Badges';
import { CLINIC_NAME, CLINIC_TAGLINE, CLINIC_ADDRESS, CLINIC_PHONE, CLINIC_EMAIL } from '../utils/clinicConfig';
import {
  Search, User, ActivitySquare, IndianRupee,
  Calendar, Clock, FlaskConical, ShoppingCart,
  Loader, ChevronDown, ChevronUp, History, Pill, Stethoscope, Printer,
  FileText, Image as ImageIcon,
} from 'lucide-react';

function fmt(n) { return (n||0).toLocaleString('en-IN',{maximumFractionDigits:0}); }
function fmtDate(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  return new Date(Number(y),Number(m)-1,Number(d)).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
}
function netTotal(b) {
  if (!b) return 0;
  const after = Math.max((b.amount||0)-(b.discount||0),0);
  return after + after*((b.taxPercent||0)/100);
}

export default function PatientTrackerPage() {
  const { showToast } = useToast();
  const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

  const [query, setQuery]         = useState('');
  const [suggestions, setSugg]    = useState([]);  // quick-search dropdown
  const [loadingSugg, setLoadSugg]= useState(false);
  const [showDrop, setShowDrop]   = useState(false);

  const [loading, setLoading]     = useState(false);
  const [data, setData]           = useState(null); // { patient, visits, labOrders, totals }
  const [multiMatch, setMultiMatch] = useState([]); // when query matches multiple patients

  const [expandedVisit, setExpandedVisit]   = useState(null);
  const [printHide, setPrintHide]           = useState({});
  const [printPreview, setPrintPreview]     = useState(false);
  // printReady: set to true after expandedVisit='__all__' settles in the DOM
  // so window.print() fires only when all visit cards are actually visible.
  const [printReady, setPrintReady]         = useState(false);
  // Editable content for print preview — doctor can customise before printing
  const [printTitle, setPrintTitle]         = useState('Patient History Report');
  const [printNotes, setPrintNotes]         = useState('');

  const debRef    = useRef(null);
  const previewRef = useRef(null); // ref to the A4 editable preview div
  const inputRef = useRef(null);
  const dropRef  = useRef(null);

  // ── Suggestion search (live dropdown) ────────────────────────────────────
  const fetchSugg = useCallback(async (q) => {
    if (q.trim().length < 2) { setSugg([]); setShowDrop(false); return; }
    setLoadSugg(true);
    try {
      const res = await api.get('/patients/search', { params: { q: q.trim() } });
      setSugg(res.data.patients || []);
      setShowDrop(true);
    } catch { setSugg([]); }
    finally { setLoadSugg(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchSugg(query), 280);
    return () => clearTimeout(debRef.current);
  }, [query, fetchSugg]);

  useEffect(() => {
    function h(e) {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) setShowDrop(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Full history fetch ────────────────────────────────────────────────────
  const fetchHistory = useCallback(async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    setData(null);
    setMultiMatch([]);
    setShowDrop(false);
    try {
      const res = await api.get('/patients/history', { params: { q: q.trim() } });
      if (res.data.patients.length > 1 && !res.data.visits?.length) {
        // Multiple patients matched and no unique pick — show a disambiguation list
        setMultiMatch(res.data.patients);
      } else {
        setData(res.data);
        setExpandedVisit(res.data.visits?.[0]?._id || null); // expand most recent
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not find patient.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Arriving here via a "View History" link (e.g. from Today's Queue) with
  // ?uid=PT-000022 in the URL — pre-fill the search and load it immediately,
  // instead of making the user re-type the patient's ID.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const uid = searchParams.get('uid');
    if (uid) {
      setQuery(uid);
      fetchHistory(uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickSuggestion(p) {
    setQuery(p.patientUID);
    setShowDrop(false);
    fetchHistory(p.patientUID);
  }

  function handleSearch(e) {
    e.preventDefault();
    fetchHistory(query);
  }

  // When printReady is set, give the browser one more frame to finish painting
  // all the newly-expanded visit cards, then fire window.print().
  useEffect(() => {
    if (!printReady) return;
    const id = setTimeout(() => {
      const prev = document.title;
      document.title = CLINIC_NAME;
      window.print();
      document.title = prev;
      setPrintReady(false);
    }, 100);
    return () => clearTimeout(id);
  }, [printReady]);

  const p   = data?.patient;
  const vs  = data?.visits  || [];
  const lo  = data?.labOrders || [];
  const ph  = data?.pharmacyHistory || [];
  const t   = data?.totals || {};

  const buys  = lo.filter((o) => o.type === 'buy');
  const sells = lo.filter((o) => o.type === 'sell');

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Patient Tracker" subtitle="Full history, visits, billing, and lab records for any patient">

      {/* Search bar */}
      <div className="card no-print" style={{ padding:20, marginBottom:20 }}>
        <form onSubmit={handleSearch} className="tracker-search" style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:260, position:'relative' }}>
            <label className="label">Search by Name, Patient ID, or Phone Number</label>
            <div style={{ position:'relative' }} ref={inputRef}>
              <Search size={16} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', pointerEvents:'none' }} />
              <input
                className="input"
                style={{ paddingLeft:38, paddingRight:36 }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. Priya, PT-000002, or 9876543211"
                autoComplete="off"
              />
              {loadingSugg && <Loader size={14} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', animation:'spin 1s linear infinite' }} />}
            </div>

            {/* Suggestions dropdown */}
            {showDrop && suggestions.length > 0 && (
              <div ref={dropRef} className="card" style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:300, padding:0, maxHeight:260, overflowY:'auto', boxShadow:'var(--shadow-lg)' }}>
                {suggestions.map((s) => (
                  <button key={s._id} type="button" onClick={() => pickSuggestion(s)}
                    style={{ width:'100%', textAlign:'left', padding:'11px 16px', border:'none', background:'transparent', cursor:'pointer', borderBottom:'1px solid var(--color-border)', display:'flex', gap:12, alignItems:'center' }}
                    onMouseEnter={(e) => e.currentTarget.style.background='var(--color-primary-soft)'}
                    onMouseLeave={(e) => e.currentTarget.style.background='transparent'}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background:'var(--color-primary-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'var(--color-primary)', fontSize:14, flexShrink:0 }}>
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13 }}>{s.name}</div>
                      <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>
                        {s.patientUID}{s.phone && ` · ${s.phone}`}{s.age && ` · ${s.age}y`}
                      </div>
                      {s.tokenDate && <div style={{ fontSize:11, color:'var(--color-ink-faint)', marginTop:1 }}>Last visit: {s.tokenDate}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || !query.trim()} style={{ alignSelf:'flex-end' }}>
            <ActivitySquare size={16} />{loading ? 'Searching…' : 'View History'}
          </button>
        </form>
      </div>

      {/* Multi-match disambiguation */}
      {multiMatch.length > 0 && (
        <div className="card no-print" style={{ padding:20, marginBottom:20 }}>
          <div style={{ fontWeight:700, marginBottom:14 }}>Multiple patients found — select one to view their full history:</div>
          <div style={{ display:'grid', gap:10 }}>
            {multiMatch.map((m) => (
              <button key={m._id} type="button" onClick={() => pickSuggestion(m)}
                style={{ textAlign:'left', padding:'12px 16px', border:'1px solid var(--color-border)', borderRadius:10, background:'var(--color-surface)', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--color-primary-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:15, color:'var(--color-primary)', flexShrink:0 }}>
                  {m.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight:700 }}>{m.name}</div>
                  <div style={{ fontSize:12, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{m.patientUID}{m.phone && ` · ${m.phone}`}{m.age && ` · ${m.age}y, ${m.gender}`}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Patient profile + summary */}
      {data && p && (
        <div style={{ display:'grid', gap:20 }}>

          {/* Print-only clinic header — invisible on screen, shows on printed page */}
          <div className="print-only-header" style={{ display:'none', textAlign:'center', marginBottom:16, paddingBottom:12, borderBottom:'2px solid #1e293b' }}>
            <div style={{ fontSize:22, fontWeight:800 }}>{CLINIC_NAME}</div>
            {CLINIC_ADDRESS && <div style={{ fontSize:11, color:'#475569', marginTop:3 }}>📍 {CLINIC_ADDRESS}</div>}
            {CLINIC_PHONE && <div style={{ fontSize:11, color:'#475569' }}>📱 {CLINIC_PHONE}{CLINIC_EMAIL ? `  ✉ ${CLINIC_EMAIL}` : ''}</div>}
            <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>{printTitle || CLINIC_TAGLINE}</div>
          </div>

          {/* Patient card with print controls */}
          <div className="card" style={{ padding:22 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:18, flexWrap:'wrap' }}>
              <div style={{ width:56, height:56, borderRadius:'50%', background:'var(--color-primary-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:22, color:'var(--color-primary)', flexShrink:0 }}>
                {p.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ fontSize:20, fontWeight:800, marginBottom:4 }}>{p.name}</div>
                <div style={{ fontSize:13, color:'var(--color-ink-faint)', display:'flex', gap:16, flexWrap:'wrap' }}>
                  <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--color-primary)' }}>{p.patientUID}</span>
                  {p.age && <span><User size={12} style={{ marginRight:3, verticalAlign:'middle' }} />{p.age}y, {p.gender}</span>}
                  {p.phone && <span className={printHide.phone ? 'print-hide' : ''}>📱 {p.phone}</span>}
                  {p.address && <span>📍 {p.address}</span>}
                </div>
                {p.medicalHistory && (
                  <div className={printHide.medical ? 'print-hide' : ''} style={{ marginTop:8, fontSize:12, color:'var(--color-ink-soft)', padding:'6px 10px', background:'var(--color-surface-raised)', borderRadius:6 }}>
                    <strong>Medical History:</strong> {p.medicalHistory}
                  </div>
                )}
              </div>
              {/* Print controls — .no-print hides this entire block in final output */}
              <div className="no-print" style={{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' }}>
                <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontWeight:600 }}>Hide from print:</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5, justifyContent:'flex-end' }}>
                  {[
                    ['phone',   '📱 Phone'],
                    ['billing', '₹ Costs & Prices'],
                    ['medical', '🏥 Medical History'],
                    ['lab',     '🧪 Lab Orders'],
                    ['pharmacy','💊 Pharmacy'],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, cursor:'pointer', padding:'3px 8px', borderRadius:4, background: printHide[key] ? 'var(--color-primary-soft)' : 'var(--color-surface)', border:`1px solid ${printHide[key] ? 'var(--color-primary)' : 'var(--color-border)'}`, whiteSpace:'nowrap', color: printHide[key] ? 'var(--color-primary)' : 'var(--color-ink-soft)', fontWeight: printHide[key] ? 700 : 400, transition:'all 0.12s' }}>
                      <input type="checkbox" checked={!!printHide[key]}
                        onChange={e => setPrintHide(h => ({...h, [key]: e.target.checked}))}
                        style={{ width:12, height:12, accentColor:'var(--color-primary)' }} />
                      {label}
                    </label>
                  ))}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setPrintPreview(true)}
                    style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <Printer size={13} /> Preview
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    setExpandedVisit('__all__');
                    // Give React one render cycle to expand all visit cards,
                    // then printReady useEffect fires window.print().
                    setPrintReady(true);
                  }}
                    style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <Printer size={13} /> Print
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Summary stat cards */}
          <div className="stat-grid">
            <SCard label="Total Visits"     value={t.totalVisits || 0}        color="primary"  Icon={ActivitySquare} />
            <SCard label="Treatment Total"  value={`₹${fmt(t.totalBilled)}`}  color="info"     Icon={Stethoscope}    hideClass={printHide.billing ? 'print-hide' : ''} />
            <SCard label="Pharmacy Total"   value={`₹${fmt(t.totalChargedNet ?? t.totalCharged)}`} color="info"     Icon={Pill}           hideClass={printHide.billing ? 'print-hide' : ''} />
            <SCard label="Total Paid"       value={`₹${fmt(t.totalPaid)}`}    color="success"  Icon={IndianRupee}    hideClass={printHide.billing ? 'print-hide' : ''} />
            <SCard label="Pending Balance"  value={`₹${fmt(t.totalPending)}`} color="warning"  Icon={IndianRupee}    hideClass={printHide.billing ? 'print-hide' : ''} />
          </div>

          {/* Visit history */}
          <Section title="Visit History" icon={History} count={vs.length}>
            {vs.length === 0 ? (
              <div style={{ color:'var(--color-ink-faint)', fontSize:13, padding:'16px 0' }}>No visit records found.</div>
            ) : vs.map((v) => {
              const net  = netTotal(v.billing);
              const pend = Math.max(net-(v.billing?.amountPaid||0),0);
              const isOpen = expandedVisit === v._id || expandedVisit === '__all__';
              return (
                <div key={v._id} style={{ border:'1px solid var(--color-border)', borderRadius:10, overflow:'hidden', marginBottom:10 }}>
                  {/* Visit header row — always visible, click to expand */}
                  <div role="button" tabIndex={0}
                    onClick={() => setExpandedVisit(isOpen ? null : v._id)}
                    onKeyDown={e => e.key === 'Enter' && setExpandedVisit(isOpen ? null : v._id)}
                    style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', cursor:'pointer', gap:12, flexWrap:'wrap', background: isOpen ? 'var(--color-primary-soft)' : 'var(--color-surface)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, flex:1, minWidth:200, flexWrap:'wrap' }}>
                      <div style={{ fontFamily:'var(--font-mono)', fontWeight:700, fontSize:13, color:'var(--color-primary)' }}>Token #{v.tokenNumber}</div>
                      <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:13 }}>
                        <Calendar size={13} color="var(--color-ink-faint)" />{fmtDate(v.tokenDate)}
                      </div>
                      {v.appointmentSlot && (
                        <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'var(--color-primary)', fontWeight:600 }}>
                          <Clock size={12} />{v.appointmentSlot}
                        </div>
                      )}
                      <StatusBadge status={v.status} />
                      {v.reasonForVisit && <span style={{ fontSize:12, color:'var(--color-ink-soft)', fontStyle:'italic' }}>{v.reasonForVisit}</span>}
                      {v.doctorName && <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>👨‍⚕️ {v.doctorName}</span>}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <div className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right' }}>
                        <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>Billed / Paid / Pending</div>
                        <div style={{ fontSize:13, fontWeight:700 }}>₹{fmt(net)} / ₹{fmt(v.billing?.amountPaid)} / <span style={{ color:pend>0?'var(--color-warning)':'var(--color-ink-faint)' }}>₹{fmt(pend)}</span></div>
                      </div>
                      <span className={printHide.billing ? 'print-hide' : ''}><PaymentBadge status={v.billing?.paymentStatus || 'unpaid'} /></span>
                      <span className="no-print" style={{ display:'flex', alignItems:'center' }}>
                        {isOpen ? <ChevronUp size={16} color="var(--color-primary)" /> : <ChevronDown size={16} color="var(--color-ink-faint)" />}
                      </span>
                    </div>
                  </div>

                  {/* Expanded visit detail — always in DOM so @media print can show it */}
                  <div className={isOpen ? 'visit-detail' : 'visit-detail visit-detail-collapsed'} style={{ padding:'14px 16px', background:'var(--color-bg)', borderTop:'1px solid var(--color-border)' }}>
                      <div className={printHide.billing ? 'print-hide' : ''} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:14 }}>
                        {/* Billing detail */}
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--color-ink-faint)', marginBottom:8, display:'flex', alignItems:'center', gap:5 }}>
                            <IndianRupee size={13} /> Billing Detail
                          </div>
                          <table style={{ fontSize:12, width:'100%', borderCollapse:'collapse' }}>
                            <tbody>
                              {v.billing?.invoiceNumber && <TR label="Invoice" value={v.billing.invoiceNumber} />}
                              <TR label="Amount"   value={`₹${fmt(v.billing?.amount)}`} />
                              {(v.billing?.discount||0) > 0 && <TR label="Discount" value={`-₹${fmt(v.billing.discount)}`} />}
                              {(v.billing?.taxPercent||0) > 0 && <TR label="Tax" value={`${v.billing.taxPercent}%`} />}
                              <TR label="Net Total" value={`₹${fmt(net)}`} bold />
                              <TR label="Paid"      value={`₹${fmt(v.billing?.amountPaid)}`} color="var(--color-success)" />
                              <TR label="Pending"   value={`₹${fmt(pend)}`} color={pend>0?'var(--color-warning)':undefined} />
                              {v.billing?.paymentMethod && v.billing.paymentMethod !== 'none' && <TR label="Method" value={v.billing.paymentMethod} />}
                              {v.billing?.dueDate && <TR label="Due Date" value={fmtDate(v.billing.dueDate)} />}
                              {v.billing?.notes && <TR label="Notes" value={v.billing.notes} />}
                            </tbody>
                          </table>
                        </div>

                        {/* Payment history */}
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--color-ink-faint)', marginBottom:8, display:'flex', alignItems:'center', gap:5 }}>
                            <History size={13} /> Payment History
                          </div>
                          {(!v.billing?.paymentHistory || v.billing.paymentHistory.length === 0) ? (
                            <div style={{ fontSize:12, color:'var(--color-ink-faint)' }}>No payments recorded.</div>
                          ) : (
                            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                              {[...v.billing.paymentHistory].reverse().map((ph) => (
                                <div key={ph._id} style={{ fontSize:12, padding:'6px 10px', background:'var(--color-surface)', borderRadius:6, display:'flex', justifyContent:'space-between', gap:8 }}>
                                  <div>
                                    {ph.amount > 0 ? <strong>₹{ph.amount.toLocaleString('en-IN')}</strong> : <span style={{ color:'var(--color-warning)', fontWeight:700 }}>⚙ Correction</span>}
                                    {ph.amount > 0 && <span style={{ color:'var(--color-ink-faint)' }}> · {ph.method}</span>}
                                    {ph.note && <span style={{ color:'var(--color-ink-faint)' }}> · {ph.note}</span>}
                                  </div>
                                  <div style={{ color:'var(--color-ink-faint)', textAlign:'right', flexShrink:0 }}>
                                    <div>{ph.paidAt ? new Date(ph.paidAt).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : ''}</div>
                                    {ph.recordedBy && <div style={{ fontSize:11 }}>by {ph.recordedBy}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Doctor notes if any */}
                      {v.doctorNotes && (
                        <div style={{ fontSize:12, padding:'8px 12px', background:'var(--color-info-soft)', borderRadius:6, color:'var(--color-info)' }}>
                          <strong>Doctor's Notes:</strong> {v.doctorNotes}
                        </div>
                      )}

                      {/* Doctor Cost — admin only, internal clinic cost, never shown on the patient's bill */}
                      {v.doctorCost?.amount > 0 && (
                        <div className="print-hide" style={{ fontSize:12, padding:'8px 12px', background:'var(--color-warning-soft)', borderRadius:6, color:'var(--color-warning)' }}>
                          <strong>Doctor Cost (internal — not on patient bill):</strong> Dr. {v.doctorCost.doctorName} — ₹{fmt(v.doctorCost.amount)}
                          {v.doctorCost.notes ? ` (${v.doctorCost.notes})` : ''}
                        </div>
                      )}

                      {/* Attachments for this visit/Appointment — shown here, not just wherever they were uploaded */}
                      {Array.isArray(v.attachments) && v.attachments.length > 0 && (
                        <div className="print-hide" style={{ padding:'10px 12px', background:'var(--color-surface)', border:'1px solid var(--color-border)', borderRadius:6 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--color-ink-soft)', marginBottom:6 }}>
                            📎 Attachments ({v.attachments.length}) — Token #{v.tokenNumber}
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                            {v.attachments.map(a => (
                              <a key={a._id} href={`${apiOrigin}${a.filePath}`} target="_blank" rel="noopener noreferrer"
                                style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--color-primary)', textDecoration:'none' }}>
                                {a.fileType === 'pdf' ? <FileText size={14} /> : <ImageIcon size={14} />}
                                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.originalName}</span>
                                {a.fromDashboardFiles && (
                                  <span style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:600, flexShrink:0 }}>(from Dashboard Files)</span>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Affected Teeth (FDI) */}
                      {Array.isArray(v.teethSelected) && v.teethSelected.length > 0 && (
                        <div style={{ padding:'10px 12px', background:'var(--color-surface)', border:'1px solid var(--color-border)', borderRadius:6 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'var(--color-ink-faint)', marginBottom:6 }}>
                            🦷 Affected Teeth (FDI)
                          </div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                            {v.teethSelected.map(t => (
                              <span key={t} style={{ fontSize:12, fontWeight:700, padding:'3px 9px', borderRadius:100, background:'#dbeafe', color:'#2563eb', border:'1px solid #93c5fd' }}>
                                Tooth {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Treatment Done — diagnosis with prices and treatment notes */}
                      {((Array.isArray(v.diagnosis) && v.diagnosis.length > 0) || v.treatmentNotes) && (
                        <div style={{ padding:'10px 14px', background:'var(--color-accent-soft)', borderRadius:8, border:'1px solid var(--color-accent)' }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'var(--color-accent)', marginBottom:8, display:'flex', alignItems:'center', gap:5 }}>
                            <Stethoscope size={13}/> Treatment Done
                          </div>
                          {Array.isArray(v.diagnosis) && v.diagnosis.length > 0 && (
                            <div style={{ marginBottom: v.treatmentNotes ? 8 : 0 }}>
                              {/* Header row — price columns get print-hide when billing is hidden */}
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 70px 60px 70px', gap:4, fontSize:10, fontWeight:700, color:'var(--color-ink-faint)', padding:'0 0 4px', borderBottom:'1px solid var(--color-border)', marginBottom:4 }}>
                                <span>Treatment</span>
                                <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right' }}>Price ₹</span>
                                <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right' }}>Disc %</span>
                                <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right' }}>Final ₹</span>
                              </div>
                              {v.diagnosis.map((d, di) => {
                                if (typeof d === 'string') {
                                  return (
                                    <div key={di} style={{ fontSize:12, padding:'3px 0', color:'var(--color-ink-soft)' }}>
                                      <Stethoscope size={11} color="var(--color-accent)" style={{ marginRight:4, verticalAlign:'middle' }}/>{d}
                                    </div>
                                  );
                                }
                                return (
                                  <div key={di} style={{ marginBottom:4 }}>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 70px 60px 70px', gap:4, fontSize:12, padding:'3px 0', borderBottom:'1px solid rgba(0,0,0,0.04)', alignItems:'center' }}>
                                      <span style={{ fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                                        <Stethoscope size={11} color="var(--color-accent)"/>{d.name}
                                        {d.quantity > 1 && <span style={{ fontSize:10, color:'var(--color-ink-faint)' }}>×{d.quantity}</span>}
                                      </span>
                                      <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right', color:'var(--color-ink-faint)' }}>
                                        {(d.catalogPrice||0)>0 ? `₹${(d.catalogPrice||0).toLocaleString('en-IN')}` : '—'}
                                      </span>
                                      <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right', color:(d.discountPercent||0)>0 ? 'var(--color-warning)' : 'var(--color-ink-faint)', fontWeight:(d.discountPercent||0)>0?700:400 }}>
                                        {(d.discountPercent||0)>0 ? `${d.discountPercent}%` : '—'}
                                      </span>
                                      <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right', fontWeight:700, color:'var(--color-success)' }}>
                                        ₹{(d.finalPrice||0).toLocaleString('en-IN')}
                                      </span>
                                    </div>
                                    {/* Per-treatment teeth selected via FDI chart */}
                                    {d.fdiRequired && Array.isArray(d.teethForTreatment) && d.teethForTreatment.length > 0 && (
                                      <div style={{ display:'flex', flexWrap:'wrap', gap:3, padding:'3px 0 3px 14px' }}>
                                        {d.teethForTreatment.map(t => (
                                          <span key={t} style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:100, background:'#dbeafe', color:'#2563eb', border:'1px solid #93c5fd' }}>
                                            🦷 #{t}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {/* Treatment total row */}
                              {v.diagnosis.some(d => typeof d !== 'string') && (
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 70px 60px 70px', gap:4, fontSize:12, padding:'5px 0 0', fontWeight:800 }}>
                                  <span>Total</span><span/><span/>
                                  <span className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right', color:'var(--color-success)' }}>
                                    ₹{v.diagnosis.reduce((s,d)=>s+(typeof d==='string'?0:(Number(d.finalPrice)||0)),0).toLocaleString('en-IN')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {v.treatmentNotes && (
                            <div style={{ fontSize:12, color:'var(--color-ink-soft)', fontStyle:'italic' }}>{v.treatmentNotes}</div>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </Section>

          {/* LAB orders (buys) */}
          {buys.length > 0 && (
            <div className={printHide.lab ? 'print-hide' : ''}>
            <Section title="LAB Orders (Buy)" icon={FlaskConical} count={buys.length}>
              <div style={{ display:'grid', gap:8 }}>
                {buys.map((o) => (
                  <div key={o._id} style={{ padding:'10px 14px', border:'1px solid var(--color-border)', borderRadius:8, fontSize:13 }}>
                    <div style={{ fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
                      <FlaskConical size={13} color="var(--color-primary)" />{o.itemName}
                      <span style={{ fontWeight:400, color:'var(--color-ink-soft)' }}>{o.quantity} {o.unit}</span>
                      <span style={{ fontSize:11, padding:'2px 7px', borderRadius:100, background:STATUS_STYLES[o.status]?.bg, color:STATUS_STYLES[o.status]?.color, fontWeight:700 }}>{o.status}</span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
                      <span>Ordered: {fmtDate(o.orderDate)}</span>
                      {o.expectedDate && <span>Expected: {fmtDate(o.expectedDate)}</span>}
                      {o.labName && <span>Lab: {o.labName}</span>}
                      {o.cost > 0 && <span>Cost: ₹{o.cost.toLocaleString('en-IN')}</span>}
                      {o.notes && <span style={{ fontStyle:'italic' }}>{o.notes}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
            </div>
          )}

          {/* Retail sales (sells) */}
          {sells.length > 0 && (
            <div className={printHide.lab ? 'print-hide' : ''}>
            <Section title="Products Sold" icon={ShoppingCart} count={sells.length}>
              <div style={{ display:'grid', gap:8 }}>
                {sells.map((o) => (
                  <div key={o._id} style={{ padding:'10px 14px', border:'1px solid var(--color-border)', borderRadius:8, fontSize:13 }}>
                    <div style={{ fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
                      <ShoppingCart size={13} color="var(--color-success)" />{o.itemName}
                      <span style={{ fontWeight:400, color:'var(--color-ink-soft)' }}>{o.quantity} {o.unit}</span>
                      {o.category && <span style={{ fontSize:11, padding:'2px 7px', borderRadius:100, background:'var(--color-info-soft)', color:'var(--color-info)', fontWeight:600 }}>{o.category}</span>}
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
                      <span>Date: {fmtDate(o.orderDate)}</span>
                      {o.cost > 0 && <span>Price: <strong>₹{o.cost.toLocaleString('en-IN')}</strong></span>}
                      <span>Payment: {o.salePaymentMethod || 'cash'} · {o.salePaymentStatus === 'paid' ? '✓ Paid' : o.salePaymentStatus}</span>
                      {o.notes && <span style={{ fontStyle:'italic' }}>{o.notes}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
            </div>
          )}

          {/* Pharmacy history */}
          {ph.length > 0 && (
            <div className={printHide.pharmacy ? 'print-hide' : ''}>
            <Section title="Pharmacy History" icon={Pill} count={ph.length}>
              <div style={{ display:'grid', gap:8 }}>
                {ph.map((t) => (
                  <div key={t._id} style={{ padding:'10px 14px', border:'1px solid var(--color-border)', borderRadius:8, fontSize:13, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <Pill size={14} color="var(--color-success)" style={{ flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700 }}>{t.itemName}</div>
                      <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
                        <span>Date: {fmtDate(t.date)}</span>
                        <span>Qty: {t.quantity} {t.unit}</span>
                        {t.notes && <span style={{ fontStyle:'italic' }}>{t.notes}</span>}
                      </div>
                    </div>
                    <div className={printHide.billing ? 'print-hide' : ''} style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontWeight:700, color:'var(--color-success)' }}>₹{((t.sellingPrice||0)*t.quantity).toLocaleString('en-IN')}</div>
                      <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>₹{t.sellingPrice}/unit</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Totals — shown below the pharmacy items */}
              <div className={printHide.billing ? 'print-hide' : ''} style={{ marginTop:12, paddingTop:12, borderTop:'1px solid var(--color-border)', display:'flex', justifyContent:'flex-end', gap:20, flexWrap:'wrap' }}>
                {(data.totals?.pharmacyDiscount||0) > 0 && (
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>Discount</div>
                    <div style={{ fontWeight:700, color:'var(--color-warning)' }}>-₹{fmt(data.totals.pharmacyDiscount)}</div>
                  </div>
                )}
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>Total</div>
                  <div style={{ fontWeight:700, color:'var(--color-success)' }}>₹{fmt(data.totals?.totalChargedNet ?? data.totals?.totalCharged)}</div>
                </div>
              </div>
            </Section>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && !data && multiMatch.length === 0 && (
        <div className="card" style={{ padding:60, textAlign:'center', color:'var(--color-ink-faint)' }}>
          <ActivitySquare size={48} style={{ marginBottom:16, opacity:0.4 }} />
          <div style={{ fontSize:16, fontWeight:700, marginBottom:8 }}>Patient Tracker</div>
          <div style={{ fontSize:13 }}>Search by patient name, Patient ID (e.g. PT-000002), or phone number to view their complete history.</div>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:translateY(-50%) rotate(0deg)}to{transform:translateY(-50%) rotate(360deg)}}`}</style>

      {/* ── Print preview modal ──────────────────────────────────────────── */}
      {printPreview && p && (
        <div style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(0,0,0,0.7)', display:'flex', flexDirection:'column', alignItems:'center' }}
          onClick={() => setPrintPreview(false)}>

          {/* Toolbar */}
          <div style={{ width:'100%', background:'#1e293b', color:'white', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:14 }}>Print Preview — {p.name}</div>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                Hiding: {Object.entries(printHide).filter(([,v])=>v).map(([k])=>k).join(', ') || 'nothing'} &nbsp;|&nbsp;
                Edit report title and notes below before printing
              </div>
            </div>
            {/* Editable report title & footer note */}
            <div style={{ display:'flex', flexDirection:'column', gap:4, minWidth:240 }}>
              <input
                value={printTitle}
                onChange={e => setPrintTitle(e.target.value)}
                placeholder="Report title…"
                style={{ fontSize:12, padding:'4px 8px', borderRadius:4, border:'1px solid #475569', background:'#334155', color:'white', outline:'none' }}
              />
              <input
                value={printNotes}
                onChange={e => setPrintNotes(e.target.value)}
                placeholder="Footer note (optional)…"
                style={{ fontSize:11, padding:'4px 8px', borderRadius:4, border:'1px solid #475569', background:'#334155', color:'white', outline:'none' }}
              />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-sm" style={{ background:'#2563eb', color:'white' }}
                onClick={() => {
                  // Capture the edited preview HTML before closing the modal
                  const previewHtml = previewRef.current ? previewRef.current.innerHTML : null;
                  setPrintPreview(false);
                  setTimeout(() => {
                    // Create a dedicated print container with the captured HTML
                    let el = document.getElementById('tracker-print-capture');
                    if (!el) {
                      el = document.createElement('div');
                      el.id = 'tracker-print-capture';
                      document.body.appendChild(el);
                    }
                    if (previewHtml) {
                      // Print the captured (possibly edited) preview content
                      el.innerHTML = previewHtml;
                      el.style.cssText = 'display:block;width:100%;padding:0;margin:0;';
                      document.body.classList.add('print-tracker-capture');
                    } else {
                      // Fallback: print the full page
                      setExpandedVisit('__all__');
                    }
                    const prev = document.title;
                    document.title = CLINIC_NAME;
                    window.print();
                    document.title = prev;
                    el.innerHTML = '';
                    el.style.cssText = '';
                    document.body.classList.remove('print-tracker-capture');
                  }, 80);
                }}>
                <Printer size={13} /> Print Now
              </button>
              <button className="btn btn-sm" style={{ background:'#475569', color:'white' }}
                onClick={() => setPrintPreview(false)}>
                Close
              </button>
            </div>
          </div>

          {/* A4 page preview */}
          <div style={{ flex:1, overflowY:'auto', width:'100%', display:'flex', justifyContent:'center', padding:'24px 0' }}
            onClick={e => e.stopPropagation()}>
            <div ref={previewRef} style={{ background:'white', width:'210mm', minHeight:'297mm', padding:'14mm 16mm', boxShadow:'0 4px 32px rgba(0,0,0,0.5)', color:'#111', fontSize:12, lineHeight:1.5 }}>

              {/* Clinic header — editable */}
              <div style={{ textAlign:'center', marginBottom:16, borderBottom:'2px solid #1e293b', paddingBottom:12 }}>
                <div contentEditable suppressContentEditableWarning
                  style={{ fontSize:20, fontWeight:800, color:'#1e293b', outline:'none', cursor:'text', minHeight:28 }}>
                  {CLINIC_NAME}
                </div>
                {CLINIC_ADDRESS && (
                  <div contentEditable suppressContentEditableWarning
                    style={{ fontSize:11, color:'#475569', marginTop:3, outline:'none', cursor:'text' }}>
                    📍&nbsp;{CLINIC_ADDRESS}
                  </div>
                )}
                {CLINIC_PHONE && (
                  <div contentEditable suppressContentEditableWarning
                    style={{ fontSize:11, color:'#475569', outline:'none', cursor:'text' }}>
                    📱&nbsp;{CLINIC_PHONE}
                    {CLINIC_EMAIL && <>&nbsp;&nbsp;✉&nbsp;{CLINIC_EMAIL}</>}
                  </div>
                )}
                <div contentEditable suppressContentEditableWarning
                  style={{ fontSize:11, color:'#64748b', marginTop:4, outline:'none', cursor:'text', minHeight:16 }}>
                  {printTitle || CLINIC_TAGLINE}
                </div>
              </div>

              {/* Patient demographics — table layout for print reliability */}
              <div style={{ marginBottom:14, padding:'10px 14px', border:'1px solid #e2e8f0', borderRadius:6 }}>
                <div contentEditable suppressContentEditableWarning
                  style={{ fontSize:15, fontWeight:700, marginBottom:6, outline:'none', cursor:'text' }}>
                  {p.name}
                </div>
                <table style={{ fontSize:11, color:'#475569', borderCollapse:'collapse', width:'100%' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding:'1px 14px 1px 0', fontFamily:'monospace', fontWeight:700, color:'#2563eb', whiteSpace:'nowrap' }}>{p.patientUID}</td>
                      {p.age && <td style={{ padding:'1px 14px 1px 0', whiteSpace:'nowrap' }}>{p.age}y,&nbsp;{p.gender}</td>}
                      {!printHide.phone && p.phone && (
                        <td style={{ padding:'1px 14px 1px 0', whiteSpace:'nowrap' }}>
                          <span contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text' }}>📱&nbsp;{p.phone}</span>
                        </td>
                      )}
                      {p.address && (
                        <td style={{ padding:'1px 0', whiteSpace:'nowrap' }}>
                          <span contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text' }}>📍&nbsp;{p.address}</span>
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
                {!printHide.medical && p.medicalHistory && (
                  <div contentEditable suppressContentEditableWarning
                    style={{ marginTop:6, fontSize:11, color:'#475569', padding:'5px 8px', background:'#f8fafc', borderRadius:4, outline:'none', cursor:'text' }}>
                    <strong>Medical History:</strong>&nbsp;{p.medicalHistory}
                  </div>
                )}
              </div>

              {/* Summary cards — table layout for print reliability */}
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:14 }}>
                <tbody>
                  <tr>
                    {[
                      ['Total Visits',    t.totalVisits||0,             '#2563eb', false],
                      ['Treatment Total', `₹${fmt(t.totalBilled)}`,    '#0891b2', true],
                      ['Pharmacy Total',  `₹${fmt(t.totalChargedNet ?? t.totalCharged)}`,   '#0891b2', true],
                      ['Total Paid',      `₹${fmt(t.totalPaid)}`,      '#16a34a', true],
                      ['Pending',         `₹${fmt(t.totalPending)}`,   '#d97706', true],
                    ].map(([label, val, color, isMoney]) => (
                      <td key={label} style={{ width:'20%', padding:'8px 6px', border:`1px solid ${color}22`, background:`${color}08`, textAlign:'center' }}>
                        <div style={{ fontSize:10, color:'#64748b', marginBottom:2 }}>{label}</div>
                        <div contentEditable suppressContentEditableWarning
                          style={{ fontSize:13, fontWeight:700, color, outline:'none', cursor:'text' }}>
                          {isMoney && printHide.billing ? '—' : val}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>

              {/* Visit history */}
              <div style={{ fontSize:13, fontWeight:700, marginBottom:8, color:'#1e293b', borderBottom:'1px solid #e2e8f0', paddingBottom:4 }}>
                Visit History ({vs.length})
              </div>
              {vs.map((v) => {
                const vnet  = netTotal(v.billing);
                const vpend = Math.max(vnet - (v.billing?.amountPaid || 0), 0);
                return (
                  <div key={v._id} style={{ marginBottom:10, padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:6, breakInside:'avoid' }}>
                    {/* Visit header — table for print spacing reliability */}
                    <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:6 }}>
                      <tbody>
                        <tr>
                          <td style={{ verticalAlign:'top' }}>
                            <span style={{ fontWeight:700 }}>Token&nbsp;#{v.tokenNumber}</span>
                            <span style={{ marginLeft:8, color:'#64748b', fontSize:11 }}>{fmtDate(v.tokenDate)}</span>
                            {v.appointmentSlot && <span style={{ marginLeft:8, fontSize:11, color:'#2563eb' }}>🕐&nbsp;{v.appointmentSlot}</span>}
                            {v.doctorName && <span style={{ marginLeft:8, fontSize:11, color:'#475569' }}>👨‍⚕️&nbsp;{v.doctorName}</span>}
                            {v.reasonForVisit && (
                              <div contentEditable suppressContentEditableWarning
                                style={{ fontSize:11, color:'#64748b', fontStyle:'italic', marginTop:2, outline:'none', cursor:'text' }}>
                                {v.reasonForVisit}
                              </div>
                            )}
                          </td>
                          {!printHide.billing && (
                            <td style={{ textAlign:'right', verticalAlign:'top', fontSize:11, color:'#64748b', whiteSpace:'nowrap', paddingLeft:8 }}>
                              <div contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text' }}>Billed&nbsp;₹{fmt(vnet)}</div>
                              {(v.billing?.discount||0) > 0 && <div contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text', color:'#d97706' }}>Discount&nbsp;-₹{fmt(v.billing.discount)}</div>}
                              <div contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text', color:'#16a34a' }}>Paid&nbsp;₹{fmt(v.billing?.amountPaid)}</div>
                              {vpend > 0 && <div contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text', color:'#d97706' }}>Due&nbsp;₹{fmt(vpend)}</div>}
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>

                    {/* Treatments */}
                    {Array.isArray(v.diagnosis) && v.diagnosis.length > 0 && (
                      <div style={{ marginBottom:6 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#64748b', marginBottom:3 }}>TREATMENTS</div>
                        {v.diagnosis.map((d, di) => (
                          <div key={di} style={{ marginBottom:3 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'2px 0', borderBottom:'1px solid #f1f5f9' }}>
                              <span contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text', flex:1 }}>
                                • {typeof d === 'string' ? d : `${d.name}${(d.quantity||1) > 1 ? ` ×${d.quantity}` : ''}`}
                              </span>
                              {typeof d === 'object' && !printHide.billing && (d.discountPercent||0) > 0 && (
                                <span style={{ color:'#d97706', fontWeight:700, marginRight:8 }}>
                                  -{d.discountPercent}%
                                </span>
                              )}
                              {typeof d === 'object' && !printHide.billing && (
                                <span contentEditable suppressContentEditableWarning style={{ color:'#16a34a', fontWeight:700, outline:'none', cursor:'text' }}>
                                  ₹{fmt(d.finalPrice)}
                                </span>
                              )}
                            </div>
                            {typeof d === 'object' && d.fdiRequired && Array.isArray(d.teethForTreatment) && d.teethForTreatment.length > 0 && (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:3, padding:'2px 0 2px 12px' }}>
                                {d.teethForTreatment.map(t => (
                                  <span key={t} style={{ fontSize:10, fontWeight:700, padding:'1px 5px', borderRadius:100, background:'#dbeafe', color:'#2563eb' }}>
                                    🦷 #{t}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {!printHide.billing && (() => {
                          const total = v.diagnosis.reduce((s,d)=>s+(typeof d==='object'?(Number(d.finalPrice)||0):0),0);
                          return total > 0 ? (
                            <div contentEditable suppressContentEditableWarning
                              style={{ fontSize:11, fontWeight:700, color:'#16a34a', textAlign:'right', marginTop:3, outline:'none', cursor:'text' }}>
                              Total ₹{fmt(total)}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}

                    {/* Doctor notes */}
                    {v.doctorNotes && (
                      <div contentEditable suppressContentEditableWarning
                        style={{ fontSize:11, color:'#475569', marginBottom:4, outline:'none', cursor:'text', padding:'3px 6px', background:'#f0f9ff', borderRadius:3 }}>
                        <strong>Notes:</strong> {v.doctorNotes}
                      </div>
                    )}
                    {v.treatmentNotes && (
                      <div contentEditable suppressContentEditableWarning
                        style={{ fontSize:11, color:'#64748b', fontStyle:'italic', marginBottom:4, outline:'none', cursor:'text' }}>
                        {v.treatmentNotes}
                      </div>
                    )}

                    {/* Affected Teeth */}
                    {Array.isArray(v.teethSelected) && v.teethSelected.length > 0 && (
                      <div style={{ marginTop:5, padding:'6px 8px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:4 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#2563eb', marginBottom:4 }}>🦷 AFFECTED TEETH (FDI)</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                          {v.teethSelected.map(t => (
                            <span key={t} style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:100, background:'#dbeafe', color:'#2563eb', border:'1px solid #93c5fd' }}>
                              Tooth {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* LAB orders */}
              {!printHide.lab && buys.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, marginBottom:8, color:'#1e293b', borderBottom:'1px solid #e2e8f0', paddingBottom:4 }}>LAB Orders ({buys.length})</div>
                  {buys.map(o => (
                    <div key={o._id} style={{ marginBottom:6, padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:4, fontSize:11 }}>
                      <div contentEditable suppressContentEditableWarning style={{ fontWeight:600, outline:'none', cursor:'text' }}>
                        {o.itemName} — {o.quantity} {o.unit} ({o.status})
                      </div>
                      <div style={{ color:'#64748b', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
                        <span>Ordered: {fmtDate(o.orderDate)}</span>
                        {o.labName && <span>Lab: {o.labName}</span>}
                        {!printHide.billing && o.cost > 0 && <span className={printHide.billing ? 'print-hide' : ''}>Cost: ₹{o.cost.toLocaleString('en-IN')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Products sold */}
              {!printHide.lab && sells.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, marginBottom:8, color:'#1e293b', borderBottom:'1px solid #e2e8f0', paddingBottom:4 }}>Products Sold ({sells.length})</div>
                  {sells.map(o => (
                    <div key={o._id} style={{ marginBottom:6, padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:4, fontSize:11 }}>
                      <div contentEditable suppressContentEditableWarning style={{ fontWeight:600, outline:'none', cursor:'text' }}>
                        {o.itemName} — {o.quantity} {o.unit}
                      </div>
                      <div style={{ color:'#64748b', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
                        <span>{fmtDate(o.orderDate)}</span>
                        {o.category && <span>{o.category}</span>}
                        {!printHide.billing && o.cost > 0 && <span>₹{o.cost.toLocaleString('en-IN')}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pharmacy history */}
              {!printHide.pharmacy && ph.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, marginBottom:8, color:'#1e293b', borderBottom:'1px solid #e2e8f0', paddingBottom:4 }}>
                    Pharmacy History ({ph.length})
                  </div>
                  {ph.map(t => (
                    <div key={t._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4, padding:'5px 8px', border:'1px solid #e2e8f0', borderRadius:4, fontSize:11 }}>
                      <div>
                        <span contentEditable suppressContentEditableWarning style={{ fontWeight:600, outline:'none', cursor:'text' }}>{t.itemName}</span>
                        <span style={{ color:'#64748b', marginLeft:6 }}>×{t.quantity} {t.unit}</span>
                        <span style={{ color:'#64748b', marginLeft:6 }}>{fmtDate(t.date)}</span>
                      </div>
                      {!printHide.billing && (
                        <span style={{ fontWeight:700, color:'#16a34a' }}>₹{((t.sellingPrice||0)*t.quantity).toLocaleString('en-IN')}</span>
                      )}
                    </div>
                  ))}
                  {/* Totals — shown below the pharmacy items */}
                  {!printHide.billing && (
                    <div style={{ display:'flex', justifyContent:'flex-end', gap:16, marginTop:6, paddingTop:6, borderTop:'1px solid #e2e8f0' }}>
                      {(data.totals?.pharmacyDiscount||0) > 0 && (
                        <span style={{ fontSize:12, fontWeight:700, color:'#d97706' }}>Discount: -₹{fmt(data.totals.pharmacyDiscount)}</span>
                      )}
                      <span style={{ fontSize:12, fontWeight:700, color:'#16a34a' }}>Total: ₹{fmt(data.totals?.totalChargedNet ?? data.totals?.totalCharged)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Editable footer note + footer */}
              <div style={{ marginTop:24, paddingTop:10, borderTop:'1px solid #e2e8f0', fontSize:10, color:'#94a3b8' }}>
                {/* Editable notes area — always visible in preview for easy editing */}
                <textarea
                  value={printNotes}
                  onChange={e => setPrintNotes(e.target.value)}
                  placeholder="Add a note, prescription, or follow-up instruction here…"
                  style={{ width:'100%', fontSize:11, color:'#475569', padding:'6px 10px', borderRadius:4, border:'1px dashed #cbd5e1', background:'#f8fafc', resize:'vertical', minHeight:48, marginBottom:8, outline:'none', fontFamily:'inherit', lineHeight:1.5, boxSizing:'border-box' }}
                />
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span contentEditable suppressContentEditableWarning style={{ outline:'none', cursor:'text' }}>{CLINIC_NAME} — Confidential</span>
                  <span>Printed: {new Date().toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

// ── Small reusable components ──────────────────────────────────────────────────
function Section({ title, icon:Icon, count, right, children }) {
  return (
    <div className="card" style={{ padding:20 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <Icon size={18} color="var(--color-primary)" />
        <span style={{ fontWeight:700, fontSize:15 }}>{title}</span>
        <span style={{ fontSize:12, padding:'2px 8px', borderRadius:100, background:'var(--color-primary-soft)', color:'var(--color-primary)', fontWeight:700 }}>{count}</span>
        {right && <span style={{ marginLeft:'auto', fontSize:13 }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function SCard({ label, value, color, Icon, hideClass = '' }) {
  return (
    <div className={`stat-card lift3d-card${hideClass ? ' ' + hideClass : ''}`}>
      <div className="stat-card-icon" style={{ background:`var(--color-${color}-soft)` }}>
        <Icon size={18} color={`var(--color-${color})`} />
      </div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

function TR({ label, value, bold, color }) {
  return (
    <tr>
      <td style={{ padding:'3px 8px 3px 0', color:'var(--color-ink-faint)', width:'40%' }}>{label}</td>
      <td style={{ padding:'3px 0', fontWeight: bold?700:400, color: color||'var(--color-ink)' }}>{value}</td>
    </tr>
  );
}

const STATUS_STYLES = {
  pending:   { color:'var(--color-warning)', bg:'var(--color-warning-soft)' },
  ordered:   { color:'var(--color-info)',    bg:'var(--color-info-soft)' },
  received:  { color:'var(--color-success)', bg:'var(--color-success-soft)' },
  cancelled: { color:'var(--color-danger)',  bg:'var(--color-danger-soft)' },
};
